import { getStudentData, addStudent, updateStudent, deleteStudent as deleteStudentFromLocal } from '../assets/data';
import { createStudent, updateStudent as updateStudentInSupabase, deleteStudent as deleteStudentFromSupabase } from '../utils/studentsService';
import { trackEvent } from '../utils/analyticsService';

// Функция для безопасного создания URL из файла
function createSafeObjectURL(file) {
  try {
    if (file && file instanceof File) {
      return URL.createObjectURL(file);
    }
    return null;
  } catch (error) {
    console.error('Error creating object URL:', error);
    return null;
  }
}

// Функция для разбора строки price на currency и amount
function parsePrice(priceString) {
  if (!priceString || typeof priceString !== 'string') {
    return { currency: '$', amount: '' };
  }
  const currencyMatch = priceString.match(/^([^\d\s]+)/);
  const currency = currencyMatch ? currencyMatch[1] : '$';
  const amountMatch = priceString.match(/(\d+(?:\.\d+)?)/);
  const amount = amountMatch ? amountMatch[1] : '';
  return { currency, amount };
}

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Owns the profile form's field/status/banner
// change handlers plus the Save and Delete actions (create-or-update /
// delete against Supabase, with the localStorage mirror kept for
// compatibility).
export function useProfileFormActions({
  studentId,
  isNew,
  navigate,
  currentUser,
  studentData,
  setStudentData,
  markDirty,
  validationErrors,
  setValidationErrors,
  bannerImageUrl,
  setBannerImageUrl,
  fileInputRef,
  studentSaveSubmitting,
  setStudentSaveSubmitting,
  setHasUnsavedChanges,
  setShowInviteLinkModal,
  showCustomModalConfig,
}) {
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setStudentData(prevData => ({
      ...prevData,
      [name]: value
    }));
    markDirty();

    // Очищаем ошибку валидации при изменении поля
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // AI recommendations are NOT auto-regenerated on every keystroke —
    // that would burn OpenAI calls. The teacher hits "Regenerate" when ready.
  };

  const handleStatusChange = (e) => {
    const { value } = e.target;
    setStudentData(prev => ({
      ...prev,
      status: value,
      vacationFrom: (value === "on_vacation") ? prev.vacationFrom : "",
      vacationTo: (value === "on_vacation") ? prev.vacationTo : "",
      sickLeaveFrom: (value === "sick_leave") ? prev.sickLeaveFrom : "",
      sickLeaveTo: (value === "sick_leave") ? prev.sickLeaveTo : "",
      pausedResumeDate: value === "paused" ? prev.pausedResumeDate : ""
    }));
    markDirty();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const newImageUrl = createSafeObjectURL(file);
      setBannerImageUrl(newImageUrl);
      markDirty();
    }
  };

  const handleBannerUploadClick = () => {
    fileInputRef.current.click();
  };

  const validateForm = () => {
    const errors = {};

    // Email обязателен
    if (!studentData.contactEmail || !studentData.contactEmail.trim()) {
      errors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(studentData.contactEmail.trim())) {
      errors.contactEmail = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (studentSaveSubmitting) return;
    setStudentSaveSubmitting(true);
    // Save button is in the banner; scroll up so user sees status/feedback
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}

    // Валидация формы
    if (!validateForm()) {
      showCustomModalConfig({
        title: 'Validation Error',
        message: 'Please fill in all required fields correctly. Email is required.',
        type: 'error',
        showCancel: false,
        confirmText: 'OK'
      });
      setStudentSaveSubmitting(false);
      return;
    }

    try {
    if (studentId === "new" || isNew) {
        // Формируем price из priceCurrency и priceAmount
        let priceValue = null;
        let priceCurrencyValue = null;
        if (studentData.priceAmount && studentData.priceCurrency) {
          priceValue = `${studentData.priceCurrency}${studentData.priceAmount}/h`;
          priceCurrencyValue = studentData.priceCurrency;
        } else if (studentData.price) {
          priceValue = studentData.price.trim();
          // Извлекаем валюту из существующей строки price
          const parsed = parsePrice(studentData.price);
          priceCurrencyValue = parsed.currency;
        }

        // Создание нового студента в Supabase
        const studentDataForSupabase = {
          name: studentData.name,
          email: studentData.contactEmail.trim(),
          phone: studentData.contactPhone || null,
          dateOfBirth: studentData.dob || null,
          proficiency: studentData.proficiency || null,
          price: priceValue, // Сохраняем полную строку с валютой и суммой
          priceCurrency: priceCurrencyValue, // Сохраняем валюту отдельно
          status: studentData.status || 'active'
        };

        const newStudent = await createStudent(studentDataForSupabase);
        console.log('New student added to Supabase:', newStudent);
        trackEvent('student_created', { studentId: newStudent.id, teacherId: currentUser?.id || null });

        // Также сохраняем в localStorage для совместимости
        const newSlug = newStudent.slug || newStudent.invite_slug;
        const inviteLink = newSlug ? `${window.location.origin}/student-invite/${newSlug}` : (studentData.inviteLink || '');
        if (newSlug) {
          addStudent(newSlug, { ...studentData, studentId: newStudent.id, bannerImage: bannerImageUrl, slug: newSlug, inviteLink });
        }

        // Обновляем состояние компонента
        setStudentData(prev => ({ ...prev, ...newStudent, slug: newSlug || prev.slug, inviteLink }));

        showCustomModalConfig({
          title: 'Success!',
          message: `New student "${studentData.name}" has been saved successfully!\n\nInvite link is ready — you can copy it now and send to the student.`,
          type: 'success',
          showCancel: true,
          cancelText: 'Close',
          confirmText: 'Copy invite link',
          onConfirm: async () => {
            if (!inviteLink) return;
            try {
              await navigator.clipboard.writeText(inviteLink);
            } catch (_) {
              // ignore clipboard failures (user can copy from modal)
            }
            setShowInviteLinkModal(true);
          }
        });
        setHasUnsavedChanges(false);
      } else {
        // Обновление существующего студента
        // Находим ID студента
        const currentData = getStudentData();
        const existingStudent = currentData[studentId];
        const studentDbId = existingStudent?.studentId || existingStudent?.id;

        if (studentDbId) {
          // Формируем price из priceCurrency и priceAmount
          let priceValue = null;
          let priceCurrencyValue = null;
          if (studentData.priceAmount && studentData.priceCurrency) {
            priceValue = `${studentData.priceCurrency}${studentData.priceAmount}/h`;
            priceCurrencyValue = studentData.priceCurrency;
          } else if (studentData.price) {
            priceValue = studentData.price.trim();
            // Извлекаем валюту из существующей строки price
            const parsed = parsePrice(studentData.price);
            priceCurrencyValue = parsed.currency;
          }

          // Обновляем в Supabase
          const studentDataForSupabase = {
            name: studentData.name,
            email: studentData.contactEmail ? studentData.contactEmail.trim() : null,
            phone: studentData.contactPhone || null,
            dateOfBirth: studentData.dob || null,
            proficiency: studentData.proficiency || null,
            price: priceValue, // Сохраняем полную строку с валютой и суммой
            priceCurrency: priceCurrencyValue, // Сохраняем валюту отдельно
            status: studentData.status || 'active'
          };

          await updateStudentInSupabase(studentDbId, studentDataForSupabase);
          console.log('Student updated in Supabase:', studentDbId);
        }

        // Также обновляем в localStorage для совместимости
      updateStudent(studentId, { ...studentData, bannerImage: bannerImageUrl });

      showCustomModalConfig({
        title: 'Success!',
        message: `Student "${studentData.name}" has been updated successfully!`,
        type: 'success',
          showCancel: false,
          confirmText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error saving student:', error);
      showCustomModalConfig({
        title: 'Error',
        message: error.message || 'Failed to save student. Please try again.',
        type: 'error',
        showCancel: false,
        confirmText: 'OK'
      });
    } finally {
      setStudentSaveSubmitting(false);
    }
  };

  const handleDeleteProfile = () => {
    if (studentId === "new" || isNew) {
      showCustomModalConfig({
        title: 'Cannot Delete',
        message: 'Cannot delete a new student profile.',
        type: 'warning',
        showCancel: false,
        confirmText: 'OK'
      });
      return;
    }

    showCustomModalConfig({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete ${studentData.name}'s profile? This action cannot be undone.`,
      type: 'warning',
      onConfirm: async () => {
        try {
          // Resolve DB UUID (we store by slug in localStorage, by UUID in Supabase)
          const currentData = getStudentData();
          const existingStudent = currentData[studentId];
          const studentDbId = existingStudent?.studentId || existingStudent?.id;

          if (studentDbId) {
            await deleteStudentFromSupabase(studentDbId);
          }

          // Also remove local cache entry (compat)
          deleteStudentFromLocal(studentId);
          console.log('Student deleted:', { studentId, studentDbId });

          showCustomModalConfig({
            title: 'Deleted',
            message: 'Student profile has been deleted.',
            type: 'success',
            showCancel: false,
            confirmText: 'OK',
            onConfirm: () => navigate('/students'),
          });
        } catch (error) {
          console.error('Error deleting student:', error);
          showCustomModalConfig({
            title: 'Error',
            message: error.message || 'Failed to delete student. Please try again.',
            type: 'error',
            showCancel: false,
            confirmText: 'OK'
          });
        }
      },
      showCancel: true,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
  };

  return {
    handleFieldChange,
    handleStatusChange,
    handleImageUpload,
    handleBannerUploadClick,
    handleSubmit,
    handleDeleteProfile,
  };
}
